"use client"

import { useEffect, useRef, useState } from "react"

const CONSENT_KEY = "flare_cookie_consent";
const POSTHOG_READY_EVENT = "flare-posthog-ready";

export function PostHogProvider({ children }) {
  const initializedRef = useRef(false);
  const posthogRef = useRef(null);
  const [, setInitialized] = useState(false);

  useEffect(() => {
    try {
      const clientToken = process.env.NEXT_PUBLIC_POSTHOG_KEY;
      if (!clientToken || String(clientToken).trim() === "") {
        if (process.env.NODE_ENV === "development") {
          // eslint-disable-next-line no-console
          console.warn("[PostHog] Skipping initialization: NEXT_PUBLIC_POSTHOG_KEY is not set.");
        }
        return;
      }
      const getConsent = () => {
        if (typeof window === "undefined") return false;
        return window.localStorage.getItem(CONSENT_KEY);
      };

      const init = async () => {
        if (initializedRef.current) return;
        if (getConsent() !== "accepted") return;
        try {
          const posthog = (await import("posthog-js")).default;
          posthog.init(clientToken, {
            api_host: "/ingest",
            ui_host: "https://eu.posthog.com",
            defaults: '2025-05-24',
            capture_exceptions: true,
            debug: false,
          });
          posthogRef.current = posthog;
          window.__flarePosthog = posthog;
          initializedRef.current = true;
          setInitialized(true);
          window.dispatchEvent(new CustomEvent(POSTHOG_READY_EVENT));
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error("[PostHog] Initialization error:", e);
        }
      };

      const scheduleInit = () => {
        if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
          window.requestIdleCallback(init, { timeout: 2000 });
        } else {
          setTimeout(init, 0);
        }
      };

      const applyConsent = () => {
        const consent = getConsent();
        if (consent === "accepted") {
          const posthog = posthogRef.current;
          if (initializedRef.current && posthog && typeof posthog.opt_in_capturing === "function") {
            posthog.opt_in_capturing();
          } else {
            scheduleInit();
          }
          return;
        }

        if (consent === "rejected" && initializedRef.current) {
          const posthog = posthogRef.current;
          if (posthog && typeof posthog.opt_out_capturing === "function") {
            posthog.opt_out_capturing();
          }
          if (posthog && typeof posthog.reset === "function") {
            posthog.reset();
          }
        }
      };

      if (getConsent() === "accepted") {
        scheduleInit();
      }

      const handleConsentChange = () => {
        applyConsent();
      };
      window.addEventListener("flare-cookie-consent", handleConsentChange);
      return () => {
        window.removeEventListener("flare-cookie-consent", handleConsentChange);
      };
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[PostHog] Initialization error:", err);
    }
  }, [])

  return children
}
